import { useCallback } from 'react';

import queryString from 'query-string';
import { isNil } from 'lodash-es';
import { createPath } from '../utils';

/**
 * Persists the scroll position for the feed into session storage so that the athlete can be
 * positioned on the last seen entry when they navigate away from the feed and back using the
 * browsers back button.
 */
const useFeedScrollRestoration = ({
  feedType,
  page,
  numEntries // number of entries to be fetched when the athlete navigates back to the feed
}) => {
  // unique key since feed component can be mounted on different pages.
  // A page which might also have different variations of the feed within itself.
  const key = `${page}-${feedType}-scroll-top`;

  const globalHistory = window.history;

  /**
   * Saves the current scroll position and pushes a new url to the history stack.
   */
  const saveScrollPosition = useCallback(() => {
    if (isNil(sessionStorage)) {
      return;
    }
    // the scroll position is stored in sessionStorage vs localStorage since it is
    // cleared when the tab is closed.
    sessionStorage.setItem(key, window.scrollY);

    // preserve all current search params eg. dashboard?feed_type=following
    const searchParams = queryString.parse(window.location.search);
    // add new search param to the url eg. dashboard?feed_type=following&num_entries = 10
    searchParams.num_entries = numEntries;

    const path = createPath({
      search: queryString.stringify(searchParams)
    });

    // #pushState - see https://developer.mozilla.org/en-US/docs/Web/API/History/pushState
    globalHistory.pushState(null, '', path);
  }, [globalHistory, key, numEntries]);

  /**
   * Clears scroll position and removes num_entries search param from the browsers history stack
   */
  const clearScrollPosition = useCallback(() => {
    if (isNil(sessionStorage)) {
      return;
    }
    sessionStorage.removeItem(key);

    const searchParams = queryString.parse(window.location.search);
    delete searchParams.num_entries;

    const path = createPath({
      search: queryString.stringify(searchParams)
    });

    // #replaceState - see https://developer.mozilla.org/en-US/docs/Web/API/History/replaceState
    globalHistory.replaceState(null, '', path);
  }, [globalHistory, key]);

  const getScrollPosition = useCallback(() => {
    if (isNil(sessionStorage)) {
      return 0;
    }
    const value = window.sessionStorage.getItem(key);
    return value ? Number(value) : 0;
  }, [key]);

  return { getScrollPosition, saveScrollPosition, clearScrollPosition };
};

export default useFeedScrollRestoration;
